import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Pill, LogIn } from "lucide-react";

const BG_IMAGE =
    "https://images.unsplash.com/photo-1762433813475-e6b761cc23d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHNvZnQlMjBuZXV0cmFsJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NzYzNjMyN3ww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
    const { user, login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("bushra.mughal@rcn.org.uk");
    const [password, setPassword] = useState("admin123");
    const [busy, setBusy] = useState(false);

    if (user) return <Navigate to="/dashboard" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            nav("/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8F7F4]">
            {/* Left: form */}
            <div className="flex items-center justify-center p-8 lg:p-16">
                <div className="w-full max-w-md fade-in-up">
                    <div className="flex items-center gap-3 mb-10">
                        <div className="w-11 h-11 rounded-xl bg-[#1A4331] flex items-center justify-center">
                            <Pill className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <p className="font-heading text-lg font-semibold text-[#1A4331] leading-tight">RCN Pharma Intel</p>
                            <p className="text-xs uppercase tracking-widest text-[#8A9A86]">Sales Intelligence</p>
                        </div>
                    </div>

                    <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1A4331] leading-tight tracking-tight">
                        Sign in to your workspace
                    </h1>
                    <p className="text-[#475569] mt-4 leading-relaxed">
                        Allocate sales opportunities to salespeople across the UK pharmaceutical pipeline with
                        AI-powered spend likelihood intelligence.
                    </p>

                    <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
                        <div>
                            <Label htmlFor="email" className="text-[#1E293B] font-medium">Email</Label>
                            <Input
                                id="email" type="email" autoComplete="email" required
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                className="mt-1.5 h-11 bg-white border-[#E2E8F0]"
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-[#1E293B] font-medium">Password</Label>
                            <Input
                                id="password" type="password" autoComplete="current-password" required
                                value={password} onChange={(e) => setPassword(e.target.value)}
                                className="mt-1.5 h-11 bg-white border-[#E2E8F0]"
                                data-testid="login-password-input"
                            />
                        </div>
                        <Button
                            type="submit" disabled={busy}
                            className="w-full h-11 bg-[#1A4331] hover:bg-[#123022] text-white font-medium gap-2"
                            data-testid="login-submit-btn"
                        >
                            <LogIn className="w-4 h-4" />
                            {busy ? "Signing in…" : "Sign in"}
                        </Button>
                    </form>

                    <div className="mt-10 p-4 rounded-lg border border-[#E2E8F0] bg-white/60">
                        <p className="text-xs uppercase tracking-widest text-[#8A9A86] mb-2">Demo credentials</p>
                        <p className="text-sm text-[#1E293B]"><span className="font-mono">bushra.mughal@rcn.org.uk</span> / <span className="font-mono">admin123</span></p>
                        <p className="text-xs text-[#475569] mt-1">Salespeople: sarah.mitchell@rcnpharma.co.uk / sales123</p>
                    </div>
                </div>
            </div>

            {/* Right: image */}
            <div className="hidden lg:block relative overflow-hidden">
                <img src={BG_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#1A4331]/65 via-[#1A4331]/35 to-transparent" />
                <div className="absolute bottom-12 left-12 right-12 text-white fade-in-up stagger-2">
                    <p className="text-xs uppercase tracking-widest text-white/80">Royal College of Nursing</p>
                    <h2 className="font-heading text-3xl lg:text-4xl font-semibold mt-3 leading-tight max-w-md">
                        Connect nursing influence with pharmaceutical spend where it matters most.
                    </h2>
                </div>
            </div>
        </div>
    );
}
